import React, {useEffect} from "react";
import {useLocation, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {LoggedInView} from "../common/LoggedInView";
import Breadcrumbs from "../common/Breadcrumbs";
import ElogInput from "../common/form/ElogInput";
import ElogCheckbox from "../common/form/ElogCheckbox";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import ElogTextarea from "../common/form/ElogTextarea";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {Link} from "react-router-dom";
import {
    getCurrentUser,
    updateUser,
    resetUpdateUserStatus, getUsers,
} from "../../store/reducers/UsersSlice";
import {RequestStatus} from "../../utils/Enums";
import {useFormik, Form, FormikProvider} from "formik";
import {createSMSSender} from "../../store/reducers/SMSSendersSlice";

interface ModifyUserScreenProps {
}

export interface ModifyUserFormProps {
    username: string;
    password: string;
    firstname: string;
    lastname: string;
    quotesEmail?: string;
    signatue?: string;
    logistician: boolean;
    accountant: boolean;
    is_checker: boolean;
    customs: boolean;
    phone: string;
    offline: boolean;
    admin_member: boolean;
    adminMember: boolean;
    show_statistics: boolean;
    groupname?: string;
    profitGoal: number;
    customsTl: boolean;
    customsTlPosition: number | null;
    hideManaged: boolean;
}

export const ModifyUserScreen: React.FC<ModifyUserScreenProps> = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const {id} = useParams();

    const usersState = useAppSelector((state) => state.users);
    const requestStatus: RequestStatus = usersState.updateUserStatus;

    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: "Kasutajad", url: "/users"},
        {text: "Kasutaja muutmine", url: `/users/${id}/modify`},
    ];

    useEffect(() => {
        dispatch(resetUpdateUserStatus());
    }, [location]);

    useEffect(() => {
        if (id) {
            dispatch(getCurrentUser(id));
        }
    }, [id]);

    // @ts-ignore
    const formik = useFormik({
        initialValues: {
            username: usersState?.currentUser?.username ?? "",
            password: "",
            firstname: usersState?.currentUser?.firstname ?? "",
            lastname: usersState?.currentUser?.lastname ?? "",
            email: usersState?.currentUser?.email ?? "",
            emailEst: usersState?.currentUser?.email_est ?? "",
            emailFin: usersState?.currentUser?.email_fin ?? "",
            emailSwe: usersState?.currentUser?.email_swe ?? "",
            quotesEmail: usersState?.currentUser?.quotes_email ?? "",
            signature: usersState?.currentUser?.signature ?? "",
            logistician: usersState?.currentUser?.logistician ?? false,
            accountant: usersState?.currentUser?.accountant ?? false,
            customs: usersState?.currentUser?.customs ?? false,
            isChecker: usersState?.currentUser?.is_checker ?? false,
            phone: usersState?.currentUser?.phone ?? "",
            offline: usersState?.currentUser?.offline ?? false,
            adminMember: usersState?.currentUser?.admin_member ?? false,
            showStatistics: usersState?.currentUser?.show_statistics ?? false,
            profitGoal: usersState?.currentUser?.profit_goal ?? 0,
            profitGoalAnnual: usersState?.currentUser?.profit_goal_annual ?? 0,
            profitGoalCustoms: usersState?.currentUser?.profit_goal_customs ?? 0,
            profitGoalDirect: usersState?.currentUser?.profit_goal_customs_direct ?? 0,
            customsTl: usersState?.currentUser?.customs_tl ?? false,
            customsTlPosition: usersState?.currentUser?.customs_tl_position ?? null,
            hideManaged: usersState?.currentUser?.hide_managed ?? false
        },
        onSubmit: (values: any) => {
            if (id) {
                dispatch(updateUser({id, userData: values}));
            }
        },
        enableReinitialize: true
    });

    const {values, setFieldValue, handleSubmit} = formik;

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Success && (
                <div className="message msg-ok">
                    <div>
                        <h2>Andmed muudetud</h2>
                    </div>
                </div>
            )}
            {requestStatus === RequestStatus.Failed && usersState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {usersState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            <FormikProvider value={formik}>
                <Form onSubmit={handleSubmit}>
                    <div className="form">
                        <h2>Kasutaja Andmed</h2>
                        <table className="form">
                            <tbody>
                            <ElogInput label="Kasutajanimi" name="username" mandatory={true}/>
                            <ElogInput label="Parool" name="password" type="password" mandatory={true}/>
                            <ElogInput label="Eesnimi" name="firstname" mandatory={true}/>
                            <ElogInput label="Perekonnanimi" name="lastname" mandatory={true}/>
                            <ElogInput label="Telefon" name="phone" mandatory={true}/>
                            <ElogInput label="Vaikimisi email" name="email" mandatory={true}/>
                            <ElogInput label="Eesti email" name="emailEst" mandatory={true}/>
                            <ElogInput label="Soome email" name="emailFin" mandatory={true}/>
                            <ElogInput label="Rootsi email" name="emailSwe" mandatory={true}/>
                            <ElogInput label="Päringute email" name="quotesEmail"/>
                            <ElogTextarea label="Emaili signatuur" name="signature"/>
                            <ElogCheckbox label="Logistik" name="logistician"/>
                            <ElogCheckbox label="Raamatupidaja" name="accountant"/>
                            <ElogCheckbox label="On Checker" name="isChecker"/>
                            <ElogCheckbox label="Tollideklarant" name="customs"/>
                            <ElogCheckbox label="Tolli valgusfoor" name="customsTl"/>
                            {values?.customsTl ? (
                                <ElogInput label="Tolli valgusfoori positsioon" name="customsTlPosition"/>
                            ) : null}
                            <ElogCheckbox label="Eemalda hallatavatest vedudest" name="hideManaged"/>
                            <ElogCheckbox label="Offline" name="offline"/>
                            <ElogCheckbox label="Administraatori liige" name="adminMember"/>
                            <ElogCheckbox label="Kuva statistikat" name="showStatistics"/>
                            <ElogInput label="Kasumi eesmärk" name="profitGoal"/>
                            {values?.logistician ? (
                                <ElogInput label="Kasumi eesmärk aastas" name="profitGoalAnnual" />
                            ) : null}
                            {values?.customs ? (
                                <>
                                    <ElogInput label="Tollid logistikutele aasta eesmärks" name="profitGoalCustoms" />
                                    <ElogInput label="Otsetollid kokku aasta eesmärk" name="profitGoalDirect" />
                                </>
                            ) : null}

                            </tbody>
                        </table>
                    </div>
                    {requestStatus === RequestStatus.Success && (
                        <div className="message msg-ok">
                            <div>
                                <h2>Andmed muudetud</h2>
                            </div>
                        </div>
                    )}
                    {requestStatus === RequestStatus.Failed && usersState.errors.length > 0 && (
                        <div className="message msg-error">
                            <div>
                                <h2>Viga:</h2>
                                {usersState.errors.map((error, index) => (
                                    <span key={index}>{error}<br/></span>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="action clear">
                        <p>
                            <button type="submit">
                                <span><strong>Muuda</strong></span>
                            </button>
                            <Link to="/users">Tühista</Link>
                        </p>
                    </div>
                </Form>
            </FormikProvider>
        </LoggedInView>
    );
};